// =============================================================
// FileActions.js — Composant rangée d'actions par fichier
// Session E (P-FILE-ACTIONS) — porter renommer/déplacer/partager
// dans FilesInterface (onglet Fichiers chat projet) en réutilisant
// les mêmes endpoints que FilesSidebar.
//
// Usage :
//   <FileActionButtons
//     file={file}
//     projectId={projectId}
//     canShare={canShare}
//     onChanged={() => loadFilesData()}
//     onPreview={(file) => setPreviewFile(...)}
//     onAnalyze={(file) => onFileAnalyze(...)}
//   />
//
// Le composant gère ses propres modales (rename, share) et menu
// flottant (move), positionnés en `position: fixed` au-dessus de
// tout le layout.
// =============================================================

import React, { useState } from 'react';
import axios from 'axios';
import { Edit3, FolderInput, Share2, Eye, Search, Trash2 } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

// ── Bouton action discret réutilisable ───────────────────────
const IconBtn = ({ onClick, title, color, children }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      padding: 6, background: 'none', border: 'none', cursor: 'pointer',
      borderRadius: 6, color, display: 'flex', transition: 'background 0.15s'
    }}
    onMouseEnter={(e) => e.currentTarget.style.background = `${color}1a`}
    onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
  >
    {children}
  </button>
);

const FileActionButtons = ({ file, projectId, canShare, onChanged, onPreview, onAnalyze, showCore = true }) => {
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  const [movePos, setMovePos] = useState(null);
  const [foldersList, setFoldersList] = useState([]);
  const [moving, setMoving] = useState(false);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareProjects, setShareProjects] = useState([]);
  const [shareTargetProject, setShareTargetProject] = useState('');
  const [shareTargetFolder, setShareTargetFolder] = useState('');
  const [shareTargetFolders, setShareTargetFolders] = useState([]);
  const [sharing, setSharing] = useState(false);

  const token = () => localStorage.getItem('oracle_token');
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  // ── Renommer ───────────────────────────────────────────────
  const openRename = () => {
    setRenameValue(file.name || file.original_name || '');
    setRenameOpen(true);
  };

  const submitRename = async () => {
    if (!renameValue.trim() || renaming) return;
    setRenaming(true);
    try {
      await axios.put(`/api/projects/${projectId}/files/${file.id}/rename`,
        { name: renameValue.trim() }, auth());
      toast.success('Fichier renommé');
      setRenameOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur renommage');
    } finally {
      setRenaming(false);
    }
  };

  // ── Déplacer ───────────────────────────────────────────────
  const openMove = async (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMovePos({ top: rect.bottom + 4, left: Math.max(rect.right - 220, 8) });
    try {
      const res = await axios.get(`/api/projects/${projectId}/folders`, auth());
      if (res.data.success) {
        const flat = [];
        const walk = (nodes, prefix = '') => {
          for (const n of nodes) {
            flat.push({ id: n.id, name: n.name, displayName: prefix + n.name });
            if (n.children) walk(n.children, prefix + '  ');
          }
        };
        walk(res.data.tree || []);
        setFoldersList(flat);
      }
    } catch (_) { setFoldersList([]); }
  };

  const submitMove = async (targetFolderId) => {
    if (moving) return;
    setMoving(true);
    try {
      await axios.put(`/api/projects/${projectId}/files/${file.id}/move`,
        { folder_id: targetFolderId }, auth());
      toast.success(targetFolderId ? 'Fichier déplacé' : 'Fichier remis à la racine');
      setMovePos(null);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur déplacement');
    } finally {
      setMoving(false);
    }
  };

  // ── Partager cross-projet ──────────────────────────────────
  const openShare = async () => {
    setShareTargetProject('');
    setShareTargetFolder('');
    setShareTargetFolders([]);
    setShareOpen(true);
    try {
      const res = await axios.get('/api/projects', auth());
      const projects = (res.data.projects || []).filter(p => p.id !== projectId);
      setShareProjects(projects);
    } catch (_) { setShareProjects([]); }
  };

  const loadShareFolders = async (targetProjId) => {
    if (!targetProjId) { setShareTargetFolders([]); return; }
    try {
      const res = await axios.get(`/api/projects/${targetProjId}/folders`, auth());
      if (res.data.success) {
        const flat = [];
        const walk = (nodes, depth = 0) => {
          for (const n of nodes) {
            flat.push({ id: n.id, name: '  '.repeat(depth) + n.name });
            if (n.children) walk(n.children, depth + 1);
          }
        };
        walk(res.data.tree || []);
        setShareTargetFolders(flat);
      }
    } catch (_) { setShareTargetFolders([]); }
  };

  const submitShare = async () => {
    if (!shareTargetProject || sharing) return;
    setSharing(true);
    try {
      const res = await axios.post(`/api/projects/${projectId}/files/share-to-project`, {
        file_ids: [file.id],
        target_project_id: parseInt(shareTargetProject, 10),
        target_folder_id: shareTargetFolder || null
      }, auth());
      if (res.data.success) {
        toast.success(`Fichier partagé vers "${res.data.target_project}"`);
        setShareOpen(false);
        onChanged && onChanged();
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur partage');
    } finally {
      setSharing(false);
    }
  };

  // ── Suppression ────────────────────────────────────────────
  const handleDelete = async () => {
    if (!await confirmModal(`Supprimer "${file.name || file.original_name}" ?`, { danger: true, confirmText: 'Supprimer' })) return;
    try {
      await axios.delete(`/api/projects/${projectId}/files/${file.id}`, auth());
      toast.success('Fichier supprimé');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur suppression');
    }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 2 }}>
        {showCore && onPreview && (
          <IconBtn onClick={() => onPreview(file)} title="Voir le contenu" color="#3b82f6">
            <Eye size={15} />
          </IconBtn>
        )}
        {showCore && onAnalyze && (
          <IconBtn onClick={() => onAnalyze(file)} title="Analyser ce fichier" color="#a855f7">
            <Search size={15} />
          </IconBtn>
        )}
        <IconBtn onClick={openRename} title="Renommer" color="#3b82f6">
          <Edit3 size={15} />
        </IconBtn>
        <IconBtn onClick={openMove} title="Déplacer vers un dossier" color="#0ea5e9">
          <FolderInput size={15} />
        </IconBtn>
        {canShare && (
          <IconBtn onClick={openShare} title="Partager vers un autre projet" color="#8b5cf6">
            <Share2 size={15} />
          </IconBtn>
        )}
        {showCore && (
          <IconBtn onClick={handleDelete} title="Supprimer" color="#ef4444">
            <Trash2 size={15} />
          </IconBtn>
        )}
      </div>

      {/* ── Modale Renommer ──────────────────────────────── */}
      {renameOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => !renaming && setRenameOpen(false)}
        >
          <div
            style={{
              background: 'var(--card-bg)', border: '1px solid var(--oracle-border)',
              borderRadius: 10, padding: 20, minWidth: 380, maxWidth: 480,
              boxShadow: '0 20px 50px rgba(0,0,0,0.4)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Edit3 size={16} style={{ color: '#3b82f6' }} />
              <h3 style={{ margin: 0, fontSize: 14, color: 'var(--text)' }}>Renommer le fichier</h3>
            </div>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && renameValue.trim()) submitRename();
                if (e.key === 'Escape' && !renaming) setRenameOpen(false);
              }}
              autoFocus
              disabled={renaming}
              maxLength={200}
              style={{
                width: '100%', padding: '9px 12px', fontSize: 13, color: 'var(--text)',
                background: 'var(--input-bg, var(--card-bg))', border: '1px solid var(--oracle-border)',
                borderRadius: 6, outline: 'none', boxSizing: 'border-box', marginBottom: 14
              }}
              placeholder="Nouveau nom"
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRenameOpen(false)}
                disabled={renaming}
                style={{
                  padding: '7px 14px', fontSize: 12, color: 'var(--text-secondary)',
                  background: 'transparent', border: '1px solid var(--oracle-border)',
                  borderRadius: 5, cursor: renaming ? 'not-allowed' : 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={submitRename}
                disabled={renaming || !renameValue.trim()}
                style={{
                  padding: '7px 14px', fontSize: 12, color: '#fff',
                  background: renaming || !renameValue.trim() ? 'var(--oracle-border)' : '#1D4ED8',
                  border: 'none', borderRadius: 5,
                  cursor: renaming || !renameValue.trim() ? 'not-allowed' : 'pointer',
                  fontWeight: 500
                }}
              >
                {renaming ? 'Renommage…' : 'Renommer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Menu flottant Déplacer ───────────────────────── */}
      {movePos && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }} onClick={() => setMovePos(null)} />
          <div
            style={{
              position: 'fixed', top: movePos.top, left: movePos.left, zIndex: 9999,
              background: 'var(--card-bg)', border: '1px solid var(--oracle-border)',
              borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              minWidth: 220, maxHeight: 240, overflowY: 'auto', padding: '4px 0', fontSize: 11
            }}
          >
            <div style={{
              padding: '6px 10px', fontWeight: 600, color: 'var(--text-secondary)',
              borderBottom: '1px solid var(--oracle-border)', fontSize: 10, textTransform: 'uppercase'
            }}>
              Déplacer vers
            </div>
            <button
              onClick={() => submitMove(null)}
              disabled={moving}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 10px',
                border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 11,
                cursor: moving ? 'not-allowed' : 'pointer', textAlign: 'left'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--card-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <FolderInput size={11} /> Racine du projet
            </button>
            {foldersList.length > 0 && (
              <div style={{ borderTop: '1px solid var(--oracle-border)' }}>
                {foldersList.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => submitMove(f.id)}
                    disabled={moving}
                    style={{
                      display: 'block', width: '100%', padding: '6px 10px', textAlign: 'left',
                      border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 11,
                      cursor: moving ? 'not-allowed' : 'pointer', whiteSpace: 'pre'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--card-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    📁 {f.displayName}
                  </button>
                ))}
              </div>
            )}
            {foldersList.length === 0 && (
              <div style={{ padding: '6px 10px', fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Aucun sous-dossier
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Modale Partager cross-projet ─────────────────── */}
      {shareOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => !sharing && setShareOpen(false)}
        >
          <div
            style={{
              background: 'var(--card-bg)', border: '1px solid var(--oracle-border)',
              borderRadius: 10, padding: 20, minWidth: 420, maxWidth: 520,
              boxShadow: '0 20px 50px rgba(0,0,0,0.4)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Share2 size={16} style={{ color: '#8b5cf6' }} />
              <h3 style={{ margin: 0, fontSize: 14, color: 'var(--text)' }}>Partager vers un projet</h3>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                Projet cible
              </label>
              <select
                value={shareTargetProject}
                onChange={(e) => { setShareTargetProject(e.target.value); loadShareFolders(e.target.value); setShareTargetFolder(''); }}
                disabled={sharing}
                style={{
                  width: '100%', padding: '7px 10px', fontSize: 12, color: 'var(--text)',
                  background: 'var(--input-bg, var(--card-bg))', border: '1px solid var(--oracle-border)',
                  borderRadius: 5, outline: 'none', boxSizing: 'border-box'
                }}
              >
                <option value="">— Choisir un projet —</option>
                {shareProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.title || p.name}</option>
                ))}
              </select>
            </div>
            {shareTargetFolders.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  Dossier (optionnel)
                </label>
                <select
                  value={shareTargetFolder}
                  onChange={(e) => setShareTargetFolder(e.target.value)}
                  disabled={sharing}
                  style={{
                    width: '100%', padding: '7px 10px', fontSize: 12, color: 'var(--text)',
                    background: 'var(--input-bg, var(--card-bg))', border: '1px solid var(--oracle-border)',
                    borderRadius: 5, outline: 'none', boxSizing: 'border-box'
                  }}
                >
                  <option value="">— Racine —</option>
                  {shareTargetFolders.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShareOpen(false)}
                disabled={sharing}
                style={{
                  padding: '7px 14px', fontSize: 12, color: 'var(--text-secondary)',
                  background: 'transparent', border: '1px solid var(--oracle-border)',
                  borderRadius: 5, cursor: sharing ? 'not-allowed' : 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={submitShare}
                disabled={sharing || !shareTargetProject}
                style={{
                  padding: '7px 14px', fontSize: 12, color: '#fff',
                  background: sharing || !shareTargetProject ? 'var(--oracle-border)' : '#8b5cf6',
                  border: 'none', borderRadius: 5,
                  cursor: sharing || !shareTargetProject ? 'not-allowed' : 'pointer',
                  fontWeight: 500
                }}
              >
                {sharing ? 'Partage…' : 'Partager'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FileActionButtons;
